// ActionsStage.tsx
import { Col, Card, CardBody, Row, Button, CardHeader, CardFooter, Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import CommentFileModal, { PreviewItem } from '../../../commetFileModal';
import { validateImages } from '../../../../lib/filesUtils';
import CommonCardHeader from '../../../../coreComponents/CommonCardHeader';
import { formatWithOffset } from '../../../../lib/timeUtils';
import AjaxSourcedData from '../../../formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import {
  ActionsStageFlowData,
  EngagedDataRow,
  EngagementsDetails as EngagementsDetailsType,
  EngagenemtFlowStatus,
  EngagenemtStatus,
} from '../../../../types/Engagements';
import { getLeadsActivityTableColumns } from '../../../../data/formsAndTable/tables/dataTables/DataSource';
import { isNewPreview } from '../../../../utils';
import Swal from 'sweetalert2';
import { useSwalNotification } from '../../../../hooks/useSwalNotification';

type Props = {
  id: number; // engagement id
  engagement: EngagementsDetailsType;
  flowDataItem: ActionsStageFlowData; // one flow (has dataRows[])
  handleOpenCreateActionModal: (id: string) => void;
  userStatus: EngagenemtStatus;
  stageStatus: EngagenemtFlowStatus;
  updateEngadedDetailsData: (formData: FormData) => Promise<any>;
};

const ActionsStage = ({
  id,
  engagement,
  flowDataItem,
  handleOpenCreateActionModal,
  stageStatus,
  userStatus,
  updateEngadedDetailsData,
}: Props) => {
  const { t } = useTranslation();
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // comment modal state
  const [openComment, setOpenComment] = useState(false);
  const [previews, setPreviews] = useState<PreviewItem[]>([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [currentEditDataId, setCurrentEditDataId] = useState<number | null>(null);
  const [toDeleteFileUrl, setToDeleteFileUrl] = useState<string[] | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const commentRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const { showSwalNotification } = useSwalNotification();
  const makeAbs = (rel?: string) =>
    rel
      ? `${process.env.REACT_APP_BACKEND_STATIC}/${rel}`
      : 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

  // revoke object URLs on unmount
  useEffect(() => {
    return () => {
      previews.forEach((p) => {
        if (isNewPreview(p)) URL.revokeObjectURL(p.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fill in image natural dimensions once per URL
  useEffect(() => {
    let cancelled = false;
    previews.forEach((p) => {
      if (p.w && p.h) return;
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        setPreviews((prev) => {
          const copy = [...prev];
          const ix = copy.findIndex((x) => x.url === p.url);
          if (ix !== -1)
            copy[ix] = { ...copy[ix], w: img.naturalWidth || 1600, h: img.naturalHeight || 1200 };
          return copy;
        });
      };
      img.src = p.url;
    });
    return () => {
      cancelled = true;
    };
  }, [previews]);

  const handleFilesSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const { valid, errors } = validateImages(files, t);
    setUploadErrors(errors);

    if (valid.length) {
      const newPreviews: PreviewItem[] = valid.map((file) => ({
        kind: 'new',
        file,
        url: URL.createObjectURL(file),
      }));
      setPreviews((prev) => [...prev, ...newPreviews]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemovePreview = async (p: PreviewItem) => {
    if (isNewPreview(p)) {
      URL.revokeObjectURL(p.url);
      setPreviews((prev) => prev.filter((x) => x !== p));
      return;
    }
    // existing on server — optimistic remove (wire to your API if needed)
    setPreviews((prev) => prev.filter((x) => x !== p));
    try {
      // await onRemoveExistingFile?.({ flowId: flowDataItem.id, url: p.url });
    } catch {
      setPreviews((prev) => [p, ...prev]); // rollback on failure
    }
  };

  // Open modal for a specific task (data row)
  const handleOpenEditComment = (rowId: number) => {
    setCurrentEditDataId(rowId);

    const row = flowDataItem.dataRows.find((el) => el.id === rowId);
    const existingFromProps: PreviewItem[] =
      (row?.files ?? []).map((f) => ({
        kind: 'existing',
        url: makeAbs(f.url),
        name: f.name,
      })) ?? [];

    setPreviews(existingFromProps);
    setComment(row?.comment ?? '');
    setOpenComment(true);
  };

  // Submit modal data (comment + files) for the selected row
  const handleSubmitComment = async () => {
    const files = previews.filter(isNewPreview).map((p) => p.file);

    if (!comment.trim() && files.length === 0) {
      commentRef.current?.focus();
      return;
    }
    if (!currentEditDataId) return;

    const { errors } = validateImages(files, t);
    if (errors.length) {
      setUploadErrors(errors);
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append('flowDataId', String(currentEditDataId)); // EngagementFlowData.id
    formData.append('flowId', String(flowDataItem.id)); // EngagementFlow.id (optional if BE needs)
    formData.append('comment', comment.trim());
    files.forEach((file) => formData.append('screenshots', file));
    formData.append('toDeleteFileUrls', toDeleteFileUrl ? JSON.stringify(toDeleteFileUrl) : '[]');

    try {
      // TODO: call your RTK mutation here (e.g., addEngagementComment(formData)).unwrap()
      const res = await updateEngadedDetailsData(formData);
      console.log('handleSubmitComment res', res);
      setOpenComment(false);
      // cleanup previews
      previews.forEach((p) => {
        if (isNewPreview(p)) URL.revokeObjectURL(p.url);
      });
      await new Promise((resolve) => setTimeout(resolve, 500));
      showSwalNotification({
        title: t('leads.notifications.successTitle'),
        text: t('leads.notifications.successText'),
        icon: 'success',
      });

      setPreviews((prev) => prev.filter((p) => p.kind === 'existing'));
      setComment('');
      setUploadErrors([]);
    } catch (error) {
      console.error('handleSubmitComment error', error);

      showSwalNotification({
        icon: 'error',
        title: t('leads.notifications.errorTitle'),
        text: t('leads.notifications.errorText'),
      });
    } finally {
      setSubmitting(false);
      setCurrentEditDataId(null);
    }
  };
  const handleDeleteFile = async (p: PreviewItem) => {
    Swal.fire({
      title: t('global.filesMutationsText.titleDelete'),
      text: t('global.filesMutationsText.textDelete'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('global.filesMutationsText.delete'),
      cancelButtonText: t('global.cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        setToDeleteFileUrl((prev) => (prev ? [...prev, p.url] : [p.url]));
        handleRemovePreview(p);
      } else if (result.isDismissed) {
        return;
      }
    });
  };
  return (
    <>
      <Row className="pt-3">
        <Card>
          <CardHeader>
            <Col sm="12" className="d-flex gap-2">
              <Col sm="6" className="border-end">
                <CommonCardHeader
                  title={t(`engagements.flowTitle.${flowDataItem.position}`)}
                  tag
                  tagClass=""
                />
              </Col>
              <Col sm="6" className="d-flex h-100 ps-2 align-items-center">
                <Row className="align-items-center">
                  <p className=" mb-0">
                    {formatWithOffset(engagement.updatedAt, viewerTz, undefined)}{' '}
                  </p>
                </Row>
                {/* <Row>
                  <Col sm="6">
                    <Button color="primary" outline className="mt-2">
                      {t('leads.buttons.closeStage')}
                    </Button>
                  </Col>
                </Row> */}
              </Col>
            </Col>
          </CardHeader>

          <CardBody>
            <AjaxSourcedData
              hasFilter={false}
              data={flowDataItem.dataRows}
              pagination={false}
              columns={getLeadsActivityTableColumns<EngagedDataRow>(t, viewerTz, {
                task: (r) => r.task ?? '',
                taskType: (r) => r.taskType ?? '',
                deadline: (r) => r.deadline,
                updatedAt: (r) => r.updatedAt,
                comment: (r) => r.comment ?? '',
                onActionClick: (row) => handleOpenEditComment(row.id),
                filesCount: (r) => r?.files?.length ?? 0,
              })}
              noDataComponent={<p>{t('global.noTableData')}</p>}
            />
          </CardBody>

          <CardFooter>
            <Row>
              <Col sm="auto" className="ms-auto d-flex justify-content-between">
                {handleOpenCreateActionModal && (
                  <Button
                    color="primary"
                    outline
                    onClick={() => handleOpenCreateActionModal(String(id))}
                  >
                    {t('leads.buttons.addAction')}
                  </Button>
                )}
              </Col>
            </Row>
          </CardFooter>
        </Card>
      </Row>

      <CommentFileModal
        isOpen={openComment}
        onClose={() => setOpenComment(false)}
        labels={{
          title: t('leads.commentForm.label'),
          placeholder: t('leads.commentForm.placeholder'),
          ok: t('global.ok'),
          saving: t('global.saving'),
          cancel: t('global.cancel'),
          remove: t('common.remove'),
          selectStatus: t('global.selector.selectStatus'),
        }}
        commentRef={commentRef as React.RefObject<HTMLTextAreaElement>}
        comment={comment}
        onCommentChange={setComment}
        hasFileInput={true}
        fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        onFilesSelected={handleFilesSelected}
        previews={previews}
        onRemovePreview={handleRemovePreview}
        uploadErrors={uploadErrors}
        submitting={submitting}
        onSubmit={handleSubmitComment}
        onFileDelete={handleDeleteFile}
      />
    </>
  );
};

export default ActionsStage;
